import React, { Component, ReactNode } from 'react';

interface ErrorInfo {
  componentStack: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          padding: '20px',
          border: '1px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          margin: '20px 0',
        }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#c62828' }}>
              ⚠️ Something went wrong
            </h3>
            <p style={{ margin: '0 0 12px 0' }}>
              An unexpected error occurred while loading this component.
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={this.handleRetry}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '8px',
              }}
            >
              Try Again
            </button>
            
            <button
              onClick={this.toggleDetails}
              style={{
                backgroundColor: 'transparent',
                color: '#c62828',
                border: '1px solid #c62828',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {this.state.showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          {this.state.showDetails && (
            <div style={{
              backgroundColor: '#ffcdd2',
              padding: '12px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '300px',
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Error Message:</strong>
                <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>
                  {this.state.error?.message || 'Unknown error'}
                </pre>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Error Stack:</strong>
                <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>
                  {this.state.error?.stack || 'No stack trace available'}
                </pre>
              </div>
              
              {this.state.errorInfo && (
                <div>
                  <strong>Component Stack:</strong>
                  <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;